import { useGetIdentity, useGetList, useStore } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import {
  ArrowLeft,
  ArrowRight,
  LayoutDashboard,
  Maximize2,
  Minimize2,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import type { ChartType, Contact, ContactNote, DashboardDef, DashboardWidget } from "../types";
import {
  useConfigurationContext,
  useConfigurationUpdater,
} from "../root/ConfigurationContext";
import { CHART_TYPES, type ReportConfig } from "../reports/ReportBuilder";
import { ReportWidget } from "../reports/ReportWidget";
import { DashboardActivityLog } from "./DashboardActivityLog";
import { DealsChart } from "./DealsChart";
import { GettingStarted } from "./GettingStarted";
import { HotContacts } from "./HotContacts";
import { SignalsCard } from "./SignalsCard";
import { TasksList } from "./TasksList";
import { Welcome } from "./Welcome";

/**
 * Home - Dashboard is the main surface. The upper-right selector switches
 * between the built-in Overview and any dashboard you've created; create
 * and edit dashboards right here (reports themselves are built under
 * Reports, and ready-made dashboards live in Reports → Library).
 */

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

let uid = Date.now() % 100000;
const nextUid = () => String(++uid);

export const Dashboard = () => {
  const { identity } = useGetIdentity();
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();
  const dashboards = config.dashboards ?? [];
  const [homeTab, setHomeTab] = useStore<string>(
    "slate.homeDashboard",
    "overview",
  );
  const [savedReports] = useStore<ReportConfig[]>("slate.savedReports", []);
  const [editing, setEditing] = useState(false);

  const { total: totalContact, isPending: isPendingContact } =
    useGetList<Contact>("contacts", {
      pagination: { page: 1, perPage: 1 },
    });

  const { total: totalContactNotes, isPending: isPendingContactNotes } =
    useGetList<ContactNote>("contact_notes", {
      pagination: { page: 1, perPage: 1 },
    });

  const { total: totalDeal, isPending: isPendingDeal } = useGetList<Contact>(
    "deals",
    {
      pagination: { page: 1, perPage: 1 },
    },
  );

  const isPending = isPendingContact || isPendingContactNotes || isPendingDeal;

  if (isPending) return null;

  const dashboard = dashboards.find((d) => d.id === homeTab) ?? null;

  const saveDashboards = (next: DashboardDef[]) =>
    updateConfig({ ...config, dashboards: next });
  const patchDashboard = (patch: Partial<DashboardDef>) => {
    if (!dashboard) return;
    saveDashboards(
      dashboards.map((d) => (d.id === dashboard.id ? { ...d, ...patch } : d)),
    );
  };
  const patchWidget = (id: string, patch: Partial<DashboardWidget>) => {
    if (!dashboard) return;
    patchDashboard({
      widgets: dashboard.widgets.map((w) =>
        w.id === id ? { ...w, ...patch } : w,
      ),
    });
  };
  const move = (index: number, dir: -1 | 1) => {
    if (!dashboard) return;
    const widgets = [...dashboard.widgets];
    const target = index + dir;
    if (target < 0 || target >= widgets.length) return;
    [widgets[index], widgets[target]] = [widgets[target]!, widgets[index]!];
    patchDashboard({ widgets });
  };

  const createDashboard = () => {
    const id = `dash-${nextUid()}`;
    saveDashboards([
      ...dashboards,
      { id, name: "New dashboard", widgets: [] },
    ]);
    setHomeTab(id);
    setEditing(true);
  };

  return (
    <div className="flex flex-col gap-4 mt-2">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-xs text-muted-foreground">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })}
          </p>
          <h1 className="text-2xl font-semibold">
            {greeting()}
            {identity?.fullName ? `, ${identity.fullName.split(" ")[0]}` : ""}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <LayoutDashboard className="w-4 h-4 text-muted-foreground" />
          <Select
            value={dashboard ? dashboard.id : "overview"}
            onValueChange={(v) => {
              setHomeTab(v);
              setEditing(false);
            }}
          >
            <SelectTrigger className="h-8 w-48 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="overview">Overview</SelectItem>
              {dashboards.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dashboard && (
            <Button
              variant={editing ? "secondary" : "outline"}
              size="sm"
              className="h-8"
              onClick={() => setEditing((v) => !v)}
            >
              <Pencil className="w-3.5 h-3.5" />
              {editing ? "Done" : "Edit"}
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-8" onClick={createDashboard}>
            <Plus className="w-3.5 h-3.5" /> New
          </Button>
          <Link
            to="/reports/library"
            className="text-xs text-muted-foreground hover:text-foreground px-1"
            title="Ready-made dashboards in the library"
          >
            Library
          </Link>
        </div>
      </div>

      {/* ------------------------------------------------ overview -------- */}
      {!dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-3">
            <div className="flex flex-col gap-4">
              <GettingStarted
                totalContacts={totalContact ?? 0}
                totalDeals={totalDeal ?? 0}
                totalNotes={totalContactNotes ?? 0}
              />
              {import.meta.env.VITE_IS_DEMO === "true" ? <Welcome /> : null}
              <HotContacts />
            </div>
          </div>
          <div className="md:col-span-6">
            <div className="flex flex-col gap-6">
              <SignalsCard />
              {totalDeal ? <DealsChart /> : null}
              <DashboardActivityLog />
            </div>
          </div>
          <div className="md:col-span-3">
            <TasksList />
          </div>
        </div>
      )}

      {/* ------------------------------------------------ custom ---------- */}
      {dashboard && editing && (
        <div className="flex items-center gap-2">
          <Input
            className="w-64 font-medium"
            value={dashboard.name}
            onChange={(e) => patchDashboard({ name: e.target.value })}
          />
          <Button
            variant="ghost"
            size="icon"
            aria-label="Delete dashboard"
            onClick={() => {
              saveDashboards(dashboards.filter((d) => d.id !== dashboard.id));
              setHomeTab("overview");
              setEditing(false);
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}

      {dashboard && (
        <div className="grid md:grid-cols-2 gap-4">
          {dashboard.widgets.map((w, i) => {
            const report = savedReports.find((r) => r.name === w.reportName);
            return (
              <Card
                key={w.id}
                className={w.size === "full" ? "md:col-span-2" : ""}
              >
                <CardHeader className="px-4 pb-0 flex flex-row items-center justify-between space-y-0 gap-2">
                  <CardTitle className="text-sm truncate">
                    {w.reportName}
                  </CardTitle>
                  {editing && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Select
                        value={w.chartType ?? "__report__"}
                        onValueChange={(v) =>
                          patchWidget(w.id, {
                            chartType:
                              v === "__report__" ? undefined : (v as ChartType),
                          })
                        }
                      >
                        <SelectTrigger className="h-7 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__report__">
                            Report default
                          </SelectItem>
                          {CHART_TYPES.map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" aria-label="Move earlier" onClick={() => move(i, -1)}>
                        <ArrowLeft className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" aria-label="Move later" onClick={() => move(i, 1)}>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Toggle size"
                        onClick={() =>
                          patchWidget(w.id, {
                            size: w.size === "half" ? "full" : "half",
                          })
                        }
                      >
                        {w.size === "half" ? (
                          <Maximize2 className="w-3.5 h-3.5" />
                        ) : (
                          <Minimize2 className="w-3.5 h-3.5" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Remove widget"
                        onClick={() =>
                          patchDashboard({
                            widgets: dashboard.widgets.filter(
                              (x) => x.id !== w.id,
                            ),
                          })
                        }
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="px-4 pt-2">
                  {report ? (
                    <ReportWidget
                      config={
                        w.chartType
                          ? { ...report, chartType: w.chartType }
                          : report
                      }
                      height={w.size === "full" ? 300 : 220}
                    />
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      The saved report "{w.reportName}" no longer exists.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {editing && (
            <div className="rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center gap-1 p-6 min-h-40 text-sm text-muted-foreground text-center">
              <p>Adding saved reports is temporarily unavailable</p>
              <p className="text-xs max-w-xs">
                Saved reports only lived in one browser and didn't carry over
                for the rest of your team, so this is off while that gets
                fixed properly.
              </p>
            </div>
          )}

          {dashboard.widgets.length === 0 && !editing && (
            <p className="text-sm text-muted-foreground md:col-span-2">
              This dashboard is empty - hit Edit to add reports, or grab a
              ready-made one from{" "}
              <Link to="/reports/library" className="underline">
                the library
              </Link>
              .
            </p>
          )}
        </div>
      )}
    </div>
  );
};
